import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  Building,
  Globe,
  MapPin,
  Calendar,
  Edit,
  Check,
  X,
  Camera,
  Hash,
  Briefcase,
} from "lucide-react";
import apiEndpoints from "../../apiconfig";
import {
  useMediaQuery,
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";

function SettingsPage({ showSnackbar }) {
  const currentYear = new Date().getFullYear();
  const profileAvatarSize = { xs: 96, sm: 112, md: 120 };
  const [isEditing, setIsEditing] = useState(false);
  const [tempProfileData, setTempProfileData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [domains, setDomains] = useState([]);
  const isMobile = useMediaQuery("(max-width: 425px)");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const resolveImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (typeof imagePath !== "string") return "";

    const trimmedPath = imagePath.trim();
    if (
      trimmedPath.startsWith("http://") ||
      trimmedPath.startsWith("https://") ||
      trimmedPath.startsWith("data:") ||
      trimmedPath.startsWith("blob:")
    ) {
      return trimmedPath;
    }

    return `${apiEndpoints.blob}${trimmedPath.replace(/^\/+/, "")}`;
  };

  const [profileData, setProfileData] = useState({
    userName: "",
    Email: "",
    Phone: "",
    companyName: "",
    gstno: "",
    companyEmail: "",
    companyPhone: "",
    companyAddress: "",
    state_id: "",
    city_id: "",
    state: "",
    city: "",
    domain: "",
    FoundedIn: "",
    website: "",
    profileimg_url: "",
    IsActive: "1",
  });

  const validateWebsite = (value) => {
    const trimmedValue = String(value || "").trim();
    if (!trimmedValue) return "";

    try {
      const parsedUrl = new URL(trimmedValue);
      if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
        return "Please enter a valid website URL (e.g., https://example.com).";
      }
      return "";
    } catch {
      return "Please enter a valid website URL (e.g., https://example.com).";
    }
  };

  const validateEmail = (value) => {
    const trimmedValue = String(value || "").trim();
    if (!trimmedValue) return "Please enter a valid email address.";
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)
      ? ""
      : "Please enter a valid email address.";
  };

  const validatePhone = (value) => {
    const sanitizedValue = String(value || "").replace(/\D/g, "").slice(0, 10);
    if (!sanitizedValue) return "Please enter a valid 10-digit phone number.";
    return /^\d{10}$/.test(sanitizedValue)
      ? ""
      : "Please enter a valid 10-digit phone number.";
  };

  const validateFoundedIn = (value) => {
    const sanitizedValue = String(value || "").replace(/\D/g, "").slice(0, 4);
    if (!sanitizedValue) return "";
    if (!/^\d{4}$/.test(sanitizedValue)) {
      return "Year must be exactly 4 digits.";
    }

    const year = Number(sanitizedValue);
    if (year < 1800 || year > currentYear) {
      return `Year must be between 1800 and ${currentYear}.`;
    }
    return "";
  };

  const sanitizeFieldValue = (field, rawValue) => {
    if (field === "Phone" || field === "companyPhone") {
      return String(rawValue || "").replace(/\D/g, "").slice(0, 10);
    }

    if (field === "FoundedIn") {
      return String(rawValue || "").replace(/\D/g, "").slice(0, 4);
    }

    return rawValue;
  };

  const validateFields = (data) => {
    const errors = {};

    const emailError = validateEmail(data.Email);
    if (emailError) errors.Email = emailError;

    const websiteError = validateWebsite(data.website);
    if (websiteError) errors.website = websiteError;

    const companyEmailError = validateEmail(data.companyEmail);
    if (companyEmailError) errors.companyEmail = companyEmailError;

    const phoneError = validatePhone(data.Phone);
    if (phoneError) errors.Phone = phoneError;

    const companyPhoneError = validatePhone(data.companyPhone);
    if (companyPhoneError) errors.companyPhone = companyPhoneError;

    const foundedInError = validateFoundedIn(data.FoundedIn);
    if (foundedInError) errors.FoundedIn = foundedInError;

    return errors;
  };

  const isSameErrorMap = (a = {}, b = {}) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => a[key] === b[key]);
  };

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }

    try {
      setLoadingCities(true);
      const response = await fetch(
        `${apiEndpoints.locations}?type=cities&state_id=${stateId}`
      );
      const data = await response.json();

      if (data.status === "success") {
        setCities(data.data);

        // Update city name if city_id exists in profile data
        if (profileData.city_id) {
          const selectedCity = data.data.find(
            (city) => city.city_id === profileData.city_id
          );
          if (selectedCity) {
            setProfileData((prev) => ({
              ...prev,
              city: selectedCity.city_name,
            }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // First fetch states
        setLoadingStates(true);
        const statesResponse = await fetch(
          `${apiEndpoints.locations}?type=states`
        );
        const statesData = await statesResponse.json();

        let validStates = [];
        if (statesData.status === "success") {
          validStates = statesData.data.filter(
            (state) =>
              state.state_id && state.state_name && state.state_name !== "0"
          );
          setStates(validStates);
        }

        // Then fetch profile data
        setLoadingProfile(true);
        const token = sessionStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const profileResponse = await fetch(apiEndpoints.profile, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!profileResponse.ok)
          throw new Error(`HTTP error! status: ${profileResponse.status}`);

        const profileJson = await profileResponse.json();
        if (profileJson.success && profileJson.data) {
          const apiData = profileJson.data;

          // Find the corresponding state name
          const stateName =
            validStates.find(
              (s) => String(s.state_id) === String(apiData.state_id)
            )?.state_name || "";

          // Initialize city name
          let cityName = "";

          // Fetch cities for the state if state_id exists
          if (apiData.state_id) {
            await fetchCities(apiData.state_id);
            // City name will be updated in the fetchCities function
          }

          // Get domain name (domains might not be loaded yet, will be handled in domains useEffect)
          const domainName = ""; // Will be updated when domains load

          const newProfileData = {
            userName: apiData.userName || "",
            Email: apiData.Email || "",
            Phone: apiData.Phone || "",
            companyName: apiData.companyName || "",
            gstno: apiData.gstno || "",
            companyEmail: apiData.companyEmail || "",
            companyPhone: apiData.companyPhone || "",
            companyAddress: apiData.companyAddress || "",
            state_id: apiData.state_id || "",
            city_id: apiData.city_id || "",
            state: stateName,
            city: cityName,
            domain: apiData.domain || "", // Keep ID initially, will be mapped to name later
            FoundedIn: apiData.FoundedIn || "",
            website: apiData.website || "",
            profileimg_url: apiData.profileimg_url || "",
            IsActive: apiData.IsActive || "1",
          };

          setProfileData(newProfileData);

          if (apiData.profileimg_url) {
            setProfileImage(apiData.profileimg_url);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        setError(error.message);
      } finally {
        setLoadingStates(false);
        setLoadingProfile(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch cities when state_id changes (for editing)
  useEffect(() => {
    if (isEditing && tempProfileData?.state_id) {
      fetchCities(tempProfileData.state_id);
    } else if (profileData.state_id) {
      fetchCities(profileData.state_id);
    }
  }, [
    profileData.state_id,
    profileData.city_id,
    isEditing,
    tempProfileData?.state_id,
  ]);

  // Fetch domains
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await fetch(apiEndpoints.domain_dropdown);
        const data = await response.json();

        if (data.success) {
          const activeDomains = data.data.filter(
            (domain) => domain.isActive === "1" && domain.isDeleted === "0"
          );
          setDomains(activeDomains);

          // Update domain name in profile data if domain ID exists
          if (profileData.domain) {
            const selectedDomain = activeDomains.find(
              (d) => String(d.id) === String(profileData.domain)
            );
            if (selectedDomain) {
              setProfileData((prev) => ({
                ...prev,
                domain: selectedDomain.domain_name,
              }));
            }
          }
        }
      } catch (error) {
        console.error("Error fetching domains:", error);
      }
    };

    fetchDomains();
  }, [profileData.domain]);

  const handleInputChange = async (field, value) => {
    if (!tempProfileData) return;

    const sanitizedValue = sanitizeFieldValue(field, value);
    const updatedData = {
      ...tempProfileData,
      [field]: sanitizedValue,
      ...(field === "state_id"
        ? {
          city_id: "",
          city: "",
        }
        : {}),
    };

    if (
      tempProfileData[field] === sanitizedValue &&
      !(field === "state_id" && (tempProfileData.city_id || tempProfileData.city))
    ) {
      return;
    }

    setTempProfileData(updatedData);
    const nextErrors = validateFields(updatedData);
    setValidationErrors((prevErrors) =>
      isSameErrorMap(prevErrors, nextErrors) ? prevErrors : nextErrors
    );

    // If the changed field is state_id, fetch cities for that state
    if (field === "state_id" && sanitizedValue) {
      try {
        setLoadingCities(true);
        const response = await fetch(
          `${apiEndpoints.locations}?type=cities&state_id=${sanitizedValue}`
        );
        const data = await response.json();

        if (data.status === "success") {
          setCities(data.data);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (previewImage && typeof previewImage === "string" && previewImage.startsWith("blob:")) {
        URL.revokeObjectURL(previewImage);
      }

      setSelectedImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleEditClick = () => {
    let domainValue = profileData.domain;
    if (typeof profileData.domain === "string") {
      const domainObj = domains.find(
        (d) => d.domain_name === profileData.domain
      );
      if (domainObj) {
        domainValue = domainObj.id;
      }
    }

    setTempProfileData({
      ...profileData,
      state_id: profileData.state_id,
      city_id: profileData.city_id,
      domain: domainValue,
      companyAddress: profileData.companyAddress || "",
      Phone: sanitizeFieldValue("Phone", profileData.Phone),
      companyPhone: sanitizeFieldValue("companyPhone", profileData.companyPhone),
      FoundedIn: sanitizeFieldValue("FoundedIn", profileData.FoundedIn),
    });
    setValidationErrors(validateFields({
      ...profileData,
      state_id: profileData.state_id,
      city_id: profileData.city_id,
      domain: domainValue,
      companyAddress: profileData.companyAddress || "",
      Phone: sanitizeFieldValue("Phone", profileData.Phone),
      companyPhone: sanitizeFieldValue("companyPhone", profileData.companyPhone),
      FoundedIn: sanitizeFieldValue("FoundedIn", profileData.FoundedIn),
    }));
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      const currentValidationErrors = validateFields(tempProfileData || {});
      setValidationErrors(currentValidationErrors);
      if (Object.keys(currentValidationErrors).length > 0) {
        showSnackbar("Please fix validation errors before saving.", "error");
        return;
      }

      const token = sessionStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const formData = new FormData();

      // Prepare the data to be sent
      const dataToSend = {
        ...tempProfileData,
        // Keep the IDs as they are for the API
        state_id: tempProfileData.state_id,
        city_id: tempProfileData.city_id,
        domain: tempProfileData.domain,
        companyAddress: tempProfileData.companyAddress || "",
      };

      Object.entries(dataToSend).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      if (selectedImageFile) {
        formData.append("profileImg", selectedImageFile);
      }

      const response = await fetch(apiEndpoints.profile, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        const errorMessage =
          data.error ||
          data.message ||
          (data.data && data.data.error) ||
          "Failed to update profile";
        throw new Error(errorMessage);
      }

      if (data.success) {
        // Update the profile data with the names mapped from IDs
        const updatedProfileData = {
          ...tempProfileData,
          state:
            states.find(
              (s) => String(s.state_id) === String(tempProfileData.state_id)
            )?.state_name || "",
          city:
            cities.find(
              (c) => String(c.city_id) === String(tempProfileData.city_id)
            )?.city_name || "",
          domain:
            domains.find((d) => String(d.id) === String(tempProfileData.domain))
              ?.domain_name || "",
          profileimg_url: data.profileimg_url || profileData.profileimg_url,
        };

        setProfileData(updatedProfileData);

        if (data.profileimg_url) {
          setProfileImage(data.profileimg_url);
          sessionStorage.setItem("profileimg_url", data.profileimg_url);
          window.dispatchEvent(
            new CustomEvent("profile-image-updated", {
              detail: {
                profileimg_url: data.profileimg_url,
                userName: tempProfileData?.userName || profileData.userName || "User",
              },
            })
          );
        }
        setPreviewImage(null);
        setSelectedImageFile(null);
        showSnackbar("Profile Updated Successfully", "success");
        setIsEditing(false);
      } else {
        showSnackbar(data.error || "Failed to update profile", "error");
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      showSnackbar(
        error.message || "An error occurred while saving the profile",
        "error"
      );
    }
  };

  const handleCancelClick = () => {
    if (previewImage && typeof previewImage === "string" && previewImage.startsWith("blob:")) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
    setSelectedImageFile(null);
    setIsEditing(false);
    setValidationErrors({});
  };

  useEffect(() => {
    return () => {
      if (previewImage && typeof previewImage === "string" && previewImage.startsWith("blob:")) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const renderField = (label, value, fieldName, icon, type = "text") => {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: "12px",
          backgroundColor: "rgba(248, 250, 252, 0.92)",
          border: "1px solid #E2E8F0",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: "#CBD5E1",
            backgroundColor: "#F8FAFC",
          },
        }}
      >
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              fontWeight: 700,
              color: "#64748B",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.65rem",
            }}
          >
            {React.cloneElement(icon, { size: 14, strokeWidth: 2.5 })}
            {label}
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              type={fieldName === "FoundedIn" ? "text" : type}
              value={tempProfileData[fieldName] || ""}
              onChange={(e) => handleInputChange(fieldName, e.target.value)}
              error={Boolean(validationErrors[fieldName])}
              helperText={validationErrors[fieldName] || ""}
              variant="outlined"
              size="small"
              inputProps={{
                ...(fieldName === "Phone" || fieldName === "companyPhone"
                  ? { maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }
                  : {}),
                ...(fieldName === "FoundedIn"
                  ? { maxLength: 4, inputMode: "numeric", pattern: "[0-9]*" }
                  : {}),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  minHeight: 40,
                },
              }}
            />
          ) : (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: "#1E293B",
                minHeight: "24px",
                display: "flex",
                alignItems: "center",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                fontSize: "0.95rem",
              }}
            >
              {value || `—`}
            </Typography>
          )}
        </Box>
    );
  };

  const renderDropdown = (
    label,
    value,
    fieldName,
    icon,
    options,
    isApiData = false
  ) => {
    let displayValue = "";

    if (fieldName === "domain") {
      if (typeof value === "string") {
        displayValue = value;
      } else {
        displayValue =
          domains.find((d) => String(d.id) === String(value))?.domain_name ||
          value;
      }
    } else if (fieldName === "state_id") {
      displayValue =
        states.find((s) => String(s.state_id) === String(value))?.state_name ||
        value;
    } else if (fieldName === "city_id") {
      displayValue =
        cities.find((c) => String(c.city_id) === String(value))?.city_name ||
        value;
    }

    return (
      <Box
        sx={{
          p: 2,
          borderRadius: "12px",
          backgroundColor: "rgba(248, 250, 252, 0.92)",
          border: "1px solid #E2E8F0",
          transition: "all 0.2s",
          width: "100%",
          boxSizing: "border-box",
          "&:hover": {
            borderColor: "#CBD5E1",
            backgroundColor: "#F8FAFC",
          },
        }}
      >
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              fontWeight: 700,
              color: "#64748B",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.65rem",
            }}
          >
            {React.cloneElement(icon, { size: 14, strokeWidth: 2.5 })}
            {label}
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              select
              value={tempProfileData[fieldName] || ""}
              onChange={(e) => handleInputChange(fieldName, e.target.value)}
              variant="outlined"
              size="small"
              SelectProps={{
                displayEmpty: true,
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  minHeight: 40,
                },
                "& .MuiSelect-select": {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                },
              }}
            >
              <MenuItem value="">
                <em style={{ color: "#9CA3AF" }}>Select {label}</em>
              </MenuItem>
              {options.map((option) => (
                <MenuItem
                  key={
                    fieldName === "domain"
                      ? option.id
                      : option[`${fieldName.replace("_id", "")}_id`]
                  }
                  value={
                    fieldName === "domain"
                      ? option.id
                      : option[`${fieldName.replace("_id", "")}_id`]
                  }
                >
                  {fieldName === "domain"
                    ? option.domain_name
                    : option[`${fieldName.replace("_id", "")}_name`]}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: "#1E293B",
                minHeight: "24px",
                display: "flex",
                alignItems: "center",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                fontSize: "0.95rem",
              }}
            >
              {displayValue || `—`}
            </Typography>
          )}
        </Box>
    );
  };

  const renderTextarea = (label, value, fieldName, icon) => {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: "12px",
          backgroundColor: "rgba(248, 250, 252, 0.92)",
          border: "1px solid #E2E8F0",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            fontWeight: 700,
            color: "#64748B",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.65rem",
          }}
        >
          {React.cloneElement(icon, { size: 14, strokeWidth: 2.5 })}
          {label}
        </Typography>
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={tempProfileData[fieldName] || ""}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: "8px",
              },
            }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: "#475569",
              py: 1,
              backgroundColor: "white",
              borderRadius: "8px",
              px: 1.5,
              border: "1px solid #E2E8F0",
              minHeight: "60px",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              lineHeight: 1.6,
              fontSize: "0.9rem",
            }}
          >
            {value || `—`}
          </Typography>
        )}
      </Box>
    );
  };

  if (loadingProfile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Loading profile data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" color="error.main">
        <Typography>Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background:
          "radial-gradient(circle at 0% 0%, rgba(219, 234, 254, 0.58) 0%, rgba(248, 250, 252, 0) 42%), radial-gradient(circle at 100% 0%, rgba(233, 213, 255, 0.45) 0%, rgba(248, 250, 252, 0) 40%)",
        minHeight: "100vh",
        pb: 8,
      }}
    >
      <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: 2, sm: 3 },
            pt: { xs: 1.5, sm: 2.5 },
          }}
      >
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.2, sm: 3, md: 4 },
            borderRadius: "18px",
            background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
            color: "#0F172A",
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: 4,
            position: "relative",
            overflow: "hidden",
            border: "1px solid #E2E8F0",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
          }}
        >
          {/* Background decoration */}
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(54, 101, 107, 0.05)",
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1, mt: { xs: 2, md: 0 } }}>
            <Box sx={{ position: "relative", width: profileAvatarSize, height: profileAvatarSize }}>
              <Avatar
                src={
                  previewImage
                    ? previewImage
                    : resolveImageUrl(profileImage)
                }
                alt="Profile"
                sx={{
                  width: profileAvatarSize,
                  height: profileAvatarSize,
                  border: "3px solid #E2E8F0",
                  bgcolor: "#ffffff"
                }}
              >
                {!resolveImageUrl(profileImage) && !previewImage && (
                  <Typography
                    variant="h3"
                    color="primary"
                    fontWeight={700}
                    sx={{ fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}
                  >
                    {profileData.userName ? profileData.userName.charAt(0).toUpperCase() : "U"}
                  </Typography>
                )}
              </Avatar>

              {isEditing && (
                <Box
                  component="label"
                  htmlFor="profile-upload"
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    bgcolor: "#ffffff",
                    borderRadius: "50%",
                    p: { xs: 0.75, sm: 1 },
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": { bgcolor: "#F3F4F6" }
                  }}
                >
                  <Camera size={18} color="#4F46E5" />
                  <input
                    id="profile-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" }, zIndex: 1 }}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: "#0F172A" }}>
              {profileData.userName || "User Name"}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent={{ xs: "center", md: "flex-start" }} gap={3} flexWrap="wrap">
              {profileData.companyName && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Building size={18} color="#64748B" />
                  <Typography variant="body1" color="#475569">
                    {profileData.companyName}
                  </Typography>
                </Box>
              )}
              {profileData.city && (
                <Box display="flex" alignItems="center" gap={1}>
                  <MapPin size={18} color="#64748B" />
                  <Typography variant="body1" color="#475569">
                    {profileData.city}, {profileData.state}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ zIndex: 1, mt: { xs: 2, md: 0 } }}>
            {!isEditing ? (
              <Button
                variant="contained"
                onClick={handleEditClick}
                startIcon={<Edit size={18} />}
                sx={{
                  bgcolor: "#36656B",
                  color: "white",
                  border: "1px solid #2B4F54",
                  px: 3,
                  py: 1.2,
                  "&:hover": { bgcolor: "#2B4F54" }
                }}
              >
                Edit Profile
              </Button>
            ) : (
              <Box display="flex" gap={2} flexWrap="wrap" justifyContent={{ xs: "center", md: "flex-start" }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelClick}
                  startIcon={<X size={18} />}
                  sx={{
                    color: "#334155",
                    borderColor: "#CBD5E1",
                    "&:hover": { borderColor: "#94A3B8", bgcolor: "#F8FAFC" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Cancel
                </Button>
                <Button
  variant="contained"
  onClick={handleSaveClick}
  startIcon={<Check size={18} />}
  disabled={Object.keys(validationErrors).length > 0}
  sx={{
    bgcolor: "#36656B",
    color: "white",
    border: "1px solid #2B4F54",
    px: 3,
    py: 1.2,
    boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
    width: { xs: "100%", sm: "auto" },
    "&:hover": { bgcolor: "#2B4F54" }
  }}
>
  Save Changes
</Button>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Profile Details Sections */}
        {/* Profile Details Sections */}
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Personal Information */}
          <Paper elevation={0} sx={{ p: { xs: 2.2, sm: 3, md: 4 }, borderRadius: "18px", border: "1px solid #E2E8F0", background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)", boxShadow: "0 10px 24px rgba(15,23,42,0.05)" }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 4, color: "#0F172A" }}>
              Personal Information
            </Typography>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(3, 1fr)" },
              gap: 3,
              width: "100%",
              boxSizing: "border-box",
            }}>
              {renderField("Username", profileData.userName, "userName", <User />)}
              {renderField("Email", profileData.Email, "Email", <Mail />, "email")}
              {renderField("Phone", profileData.Phone, "Phone", <Phone />, "tel")}
            </Box>
          </Paper>

          {/* Location & Web */}
          <Paper elevation={0} sx={{ p: { xs: 2.2, sm: 3, md: 4 }, borderRadius: "18px", border: "1px solid #E2E8F0", background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)", boxShadow: "0 10px 24px rgba(15,23,42,0.05)" }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 4, color: "#0F172A" }}>
              Location & Digital Presence
            </Typography>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(3, 1fr)" },
              alignItems: "start",
              gap: 3,
              width: "100%",
              boxSizing: "border-box",
            }}>
              {renderTextarea("Full Address", profileData.companyAddress, "companyAddress", <MapPin />)}
              {renderDropdown("State", profileData.state, "state_id", <MapPin />, states)}
              {renderDropdown("City", profileData.city, "city_id", <MapPin />, cities)}
              {renderField("Website URL", profileData.website, "website", <Globe />, "url")}
            </Box>
          </Paper>

          {/* Company Information */}
          <Paper elevation={0} sx={{ p: { xs: 2.2, sm: 3, md: 4 }, borderRadius: "18px", border: "1px solid #E2E8F0", background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)", boxShadow: "0 10px 24px rgba(15,23,42,0.05)" }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 4, color: "#0F172A" }}>
              Business Details
            </Typography>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(3, 1fr)" },
              gap: 3,
              width: "100%",
              boxSizing: "border-box",
            }}>
              {renderField("Company Name", profileData.companyName, "companyName", <Building />)}
              {renderField("GST Number", profileData.gstno, "gstno", <Hash />)}
              {renderField("Official Email", profileData.companyEmail, "companyEmail", <Mail />, "email")}
              {renderField("Office Phone", profileData.companyPhone, "companyPhone", <Phone />, "tel")}
              {renderDropdown("Industrial Domain", profileData.domain, "domain", <Briefcase />, domains)}
              {renderField("Year Founded", profileData.FoundedIn, "FoundedIn", <Calendar />, "number")}
            </Box>
          </Paper>

          {/* Mobile Save Actions (if editing) */}
          {isEditing && isMobile && (
            <Box display="flex" gap={2} mt={2}>
              <Button fullWidth variant="outlined" color="inherit" onClick={handleCancelClick} startIcon={<X />}>
                Cancel
              </Button>
<Button
  fullWidth
  variant="contained"
  onClick={handleSaveClick}
  startIcon={<Check />}
  disabled={Object.keys(validationErrors).length > 0}
  sx={{
    bgcolor: "#36656B",
    color: "white",
    border: "1px solid #2B4F54",
    px: 3,
    py: 1.2,
    "&:hover": { bgcolor: "#2B4F54" }
  }}
>
  Save
</Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default SettingsPage;